import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
// import { Controller } from 'react-hook-form'; // Unused for now
import { Strategy, StrategyConfig } from '@/types';
import { tradingPairsApi } from '@/services/api';
import LoadingSpinner from '@/components/UI/LoadingSpinner';
import toast from 'react-hot-toast';

interface StrategyFormProps {
  initialData?: Strategy | null;
  mode: 'create' | 'edit' | 'clone';
  onSubmit: (data: Partial<Strategy>) => void;
  onCancel: () => void;
}

interface FormData {
  name: string;
  description: string;
  type: 'ma_crossover' | 'momentum' | 'balanced_portfolio' | 'ai_enhanced';
  config: StrategyConfig;
  isActive: boolean;
  isPaperTrading: boolean;
  maxPositionSize: number;
  riskLevel: 'low' | 'medium' | 'high';
  stopLossPercentage?: number;
  takeProfitPercentage?: number;
  stopLossType?: 'fixed' | 'trailing';      // NEW
  takeProfitType?: 'fixed' | 'trailing';    // NEW
}

const StrategyForm: React.FC<StrategyFormProps> = ({
  initialData,
  mode,
  onSubmit,
  onCancel,
}) => {
  const [loading, setLoading] = useState(false);
  const [_tradingPairs, _setTradingPairs] = useState<any[]>([]);

  const {
    register,
    handleSubmit,
    watch,
    // control, // Unused for now
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      name: mode === 'clone' ? `${initialData?.name} (Copy)` : initialData?.name || '',
      description: initialData?.description || '',
      type: initialData?.type || 'ma_crossover',
      config: initialData?.config || {},
      isActive: mode === 'clone' ? false : initialData?.isActive ?? true,
      isPaperTrading: initialData?.isPaperTrading ?? true,
      maxPositionSize: initialData?.maxPositionSize ? initialData.maxPositionSize * 100 : 5,
      riskLevel: initialData?.riskLevel || 'medium',
      stopLossPercentage: initialData?.stopLoss || undefined,
      takeProfitPercentage: initialData?.takeProfit || undefined,
      stopLossType: initialData?.stopLossType || 'trailing',     // NEW: Default to trailing for backward compatibility
      takeProfitType: initialData?.takeProfitType || 'fixed',    // NEW: Default to fixed
    },
  });

  const selectedType = watch('type');

  useEffect(() => {
    fetchTradingPairs();
  }, []);

  useEffect(() => {
    // Set default config when strategy type changes
    // CRITICAL: Execution parameters MUST be identical across Backtest/Paper/Live trading
    const executionDefaults = {
      feeRate: 0.001,          // 0.1% trading fee (SAME for all modes)
      slippage: 0.001,         // 0.1% slippage tolerance (SAME for all modes)
      minOrderValue: 10,       // $10 minimum order (SAME for all modes)
      maxOrderValue: 100000,   // $100k maximum order (SAME for all modes)
    };

    const defaultConfigs = {
      ma_crossover: {
        ...executionDefaults,
        shortPeriod: 20,
        longPeriod: 50,
        signalPeriod: 9,
        minConfidence: 0.6,
        volumeThreshold: 1.2,
        rsiFilter: true,
        rsiOversold: 30,
        rsiOverbought: 70,
      },
      momentum: {
        ...executionDefaults,
        rsiPeriod: 14,
        rsiOverbought: 70,
        rsiOversold: 30,
        macdFast: 12,
        macdSlow: 26,
        macdSignal: 9,
        volumeThreshold: 1.2,
        minConfidence: 0.6,
        priceChangePeriod: 20,
        priceChangeThreshold: 2,       // 2% price change threshold
        fearGreedAdjustment: false,    // Disabled by default
      },
      balanced_portfolio: {
        ...executionDefaults,
        rebalanceInterval: 'weekly' as const,
        maxDeviation: 0.05,
        minTradeAmount: 10,
        targetAllocation: {},
      },
      ai_enhanced: {
        ...executionDefaults,
        baseStrategy: 'ma_crossover',
        aiWeight: 0.3,
        confidenceThreshold: 0.7,
        marketSentimentWeight: 0.2,
        newsAnalysisWeight: 0.1,
      },
    };

    if (!initialData || mode === 'create') {
      setValue('config', defaultConfigs[selectedType]);
    } else if (mode === 'edit' || mode === 'clone') {
      // Merge execution defaults with existing config to ensure new parameters are available
      const mergedConfig = {
        ...defaultConfigs[selectedType],
        ...initialData.config,
      };
      
      // Convert priceChangeThreshold from decimal (0.02) to percentage (2) for display (momentum only)
      if (selectedType === 'momentum' && 'priceChangeThreshold' in mergedConfig) {
        mergedConfig.priceChangeThreshold = initialData.config.priceChangeThreshold !== undefined 
          ? initialData.config.priceChangeThreshold * 100 
          : mergedConfig.priceChangeThreshold;
      }
      
      setValue('config', mergedConfig);
    }
  }, [selectedType, setValue, initialData, mode]);

  const fetchTradingPairs = async () => {
    try {
      const response = await tradingPairsApi.getAll();
      _setTradingPairs(response.data || []);
    } catch (error) {
      console.error('Error fetching trading pairs:', error);
    }
  };

  const handleFormSubmit = async (data: FormData) => {
    try {
      setLoading(true);
      // Convert percentage (1-100) to decimal (0.01-1.00) for database
      const formattedData: any = {
        ...data,
        maxPositionSize: data.maxPositionSize / 100,
        config: {
          ...data.config,
        },
        // Map form fields to Strategy interface
        stopLoss: data.stopLossPercentage,
        takeProfit: data.takeProfitPercentage,
        stopLossType: data.stopLossType,      // NEW
        takeProfitType: data.takeProfitType,  // NEW
      };
      
      // Remove form-specific fields
      delete formattedData.stopLossPercentage;
      delete formattedData.takeProfitPercentage;
      
      // Convert priceChangeThreshold from percentage (2) to decimal (0.02) for momentum strategy
      if (data.type === 'momentum' && 'priceChangeThreshold' in data.config) {
        formattedData.config.priceChangeThreshold = data.config.priceChangeThreshold !== undefined 
          ? data.config.priceChangeThreshold / 100 
          : data.config.priceChangeThreshold;
      }
      
      await onSubmit(formattedData);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to save strategy');
    } finally {
      setLoading(false);
    }
  };

  const renderStrategyConfig = () => {
    // const _config = watch('config') || {}; // Unused for now

    switch (selectedType) {
      case 'ma_crossover':
        return (
          <div className="space-y-4">
            <h4 className="text-md font-medium text-gray-900">MA Crossover Configuration</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Short Period *
                </label>
                <input
                  type="number"
                  min="5"
                  max="100"
                  {...register('config.shortPeriod', {
                    required: 'Short period is required',
                    min: { value: 5, message: 'Minimum value is 5' },
                    max: { value: 100, message: 'Maximum value is 100' },
                    valueAsNumber: true,
                  })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {errors.config?.shortPeriod && (
                  <p className="mt-1 text-sm text-red-600">{errors.config.shortPeriod.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Long Period *
                </label>
                <input
                  type="number"
                  min="10"
                  max="200"
                  {...register('config.longPeriod', {
                    required: 'Long period is required',
                    min: { value: 10, message: 'Minimum value is 10' },
                    max: { value: 200, message: 'Maximum value is 200' },
                    valueAsNumber: true,
                  })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {errors.config?.longPeriod && (
                  <p className="mt-1 text-sm text-red-600">{errors.config.longPeriod.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Signal Period
                </label>
                <input
                  type="number"
                  min="3"
                  max="50"
                  {...register('config.signalPeriod', {
                    min: { value: 3, message: 'Minimum value is 3' },
                    max: { value: 50, message: 'Maximum value is 50' },
                    valueAsNumber: true,
                  })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Min Confidence
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.1"
                  max="1"
                  {...register('config.minConfidence', {
                    min: { value: 0.1, message: 'Minimum value is 0.1' },
                    max: { value: 1, message: 'Maximum value is 1' },
                    valueAsNumber: true,
                  })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Volume Threshold
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0.5"
                  max="5"
                  {...register('config.volumeThreshold', {
                    min: { value: 0.5, message: 'Minimum value is 0.5' },
                    max: { value: 5, message: 'Maximum value is 5' },
                    valueAsNumber: true,
                  })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Minimum volume ratio (1.2 = 120%)
                </p>
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                {...register('config.rsiFilter')}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-700">
                Enable RSI Filter
              </label>
            </div>

            {watch('config.rsiFilter') && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    RSI Oversold
                  </label>
                  <input
                    type="number"
                    min="10"
                    max="50"
                    {...register('config.rsiOversold', {
                      min: { value: 10, message: 'Minimum value is 10' },
                      max: { value: 50, message: 'Maximum value is 50' },
                      valueAsNumber: true,
                    })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    RSI Overbought
                  </label>
                  <input
                    type="number"
                    min="50"
                    max="90"
                    {...register('config.rsiOverbought', {
                      min: { value: 50, message: 'Minimum value is 50' },
                      max: { value: 90, message: 'Maximum value is 90' },
                      valueAsNumber: true,
                    })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
            )}
          </div>
        );

      case 'momentum':
        return (
          <div className="space-y-4">
            <h4 className="text-md font-medium text-gray-900">Momentum Strategy Configuration</h4>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  RSI Period
                </label>
                <input
                  type="number"
                  min="5"
                  max="50"
                  {...register('config.rsiPeriod', {
                    min: { value: 5, message: 'Minimum value is 5' },
                    max: { value: 50, message: 'Maximum value is 50' },
                    valueAsNumber: true,
                  })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  RSI Oversold
                </label>
                <input
                  type="number"
                  min="10"
                  max="50"
                  {...register('config.rsiOversold', {
                    min: { value: 10, message: 'Minimum value is 10' },
                    max: { value: 50, message: 'Maximum value is 50' },
                    valueAsNumber: true,
                  })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  RSI Overbought
                </label>
                <input
                  type="number"
                  min="50"
                  max="90"
                  {...register('config.rsiOverbought', {
                    min: { value: 50, message: 'Minimum value is 50' },
                    max: { value: 90, message: 'Maximum value is 90' },
                    valueAsNumber: true,
                  })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  MACD Fast
                </label>
                <input
                  type="number"
                  min="5"
                  max="30"
                  {...register('config.macdFast', {
                    min: { value: 5, message: 'Minimum value is 5' },
                    max: { value: 30, message: 'Maximum value is 30' },
                    valueAsNumber: true,
                  })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  MACD Slow
                </label>
                <input
                  type="number"
                  min="15"
                  max="50"
                  {...register('config.macdSlow', {
                    min: { value: 15, message: 'Minimum value is 15' },
                    max: { value: 50, message: 'Maximum value is 50' },
                    valueAsNumber: true,
                  })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  MACD Signal
                </label>
                <input
                  type="number"
                  min="5"
                  max="20"
                  {...register('config.macdSignal', {
                    min: { value: 5, message: 'Minimum value is 5' },
                    max: { value: 20, message: 'Maximum value is 20' },
                    valueAsNumber: true,
                  })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Volume Threshold
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0.5"
                  max="5"
                  {...register('config.volumeThreshold', {
                    min: { value: 0.5, message: 'Minimum value is 0.5' },
                    max: { value: 5, message: 'Maximum value is 5' },
                    valueAsNumber: true,
                  })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Minimum volume ratio vs average (1.2 = 120% of average volume)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Min Confidence
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.1"
                  max="1"
                  {...register('config.minConfidence', {
                    min: { value: 0.1, message: 'Minimum value is 0.1' },
                    max: { value: 1, message: 'Maximum value is 1' },
                    valueAsNumber: true,
                  })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Minimum confidence threshold to execute trades (0.6 = 60%)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price Change Threshold (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  max="20"
                  {...register('config.priceChangeThreshold', {
                    min: { value: 0.1, message: 'Minimum value is 0.1%' },
                    max: { value: 20, message: 'Maximum value is 20%' },
                    valueAsNumber: true,
                  })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Minimum price change to trigger momentum signals (e.g., 2 = 2% price movement)
                </p>
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                {...register('config.fearGreedAdjustment')}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-700">
                Enable Fear & Greed Index Adjustments
              </label>
            </div>
          </div>
        );

      case 'balanced_portfolio':
        return (
          <div className="space-y-4">
            <h4 className="text-md font-medium text-gray-900">Balanced Portfolio Configuration</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rebalance Interval
                </label>
                <select
                  {...register('config.rebalanceInterval')}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Deviation (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max="0.5"
                  {...register('config.maxDeviation', {
                    min: { value: 0.01, message: 'Minimum value is 0.01' },
                    max: { value: 0.5, message: 'Maximum value is 0.5' },
                    valueAsNumber: true,
                  })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Min Trade Amount ($)
              </label>
              <input
                type="number"
                min="1"
                max="1000"
                {...register('config.minTradeAmount', {
                  min: { value: 1, message: 'Minimum value is 1' },
                  max: { value: 1000, message: 'Maximum value is 1000' },
                  valueAsNumber: true,
                })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        );

      case 'ai_enhanced':
        return (
          <div className="space-y-4">
            <h4 className="text-md font-medium text-gray-900">AI Enhanced Strategy Configuration</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Base Strategy
                </label>
                <select
                  {...register('config.baseStrategy')}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="ma_crossover">MA Crossover</option>
                  <option value="momentum">Momentum</option>
                  <option value="balanced_portfolio">Balanced Portfolio</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  AI Weight
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  {...register('config.aiWeight', {
                    min: { value: 0, message: 'Minimum value is 0' },
                    max: { value: 1, message: 'Maximum value is 1' },
                    valueAsNumber: true,
                  })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confidence Threshold
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.1"
                  max="1"
                  {...register('config.confidenceThreshold', {
                    min: { value: 0.1, message: 'Minimum value is 0.1' },
                    max: { value: 1, message: 'Maximum value is 1' },
                    valueAsNumber: true,
                  })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Market Sentiment Weight
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  {...register('config.marketSentimentWeight', {
                    min: { value: 0, message: 'Minimum value is 0' },
                    max: { value: 1, message: 'Maximum value is 1' },
                    valueAsNumber: true,
                  })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Basic Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Strategy Name *
          </label>
          <input
            type="text"
            {...register('name', { required: 'Strategy name is required' })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="My Trading Strategy"
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            {...register('description')}
            rows={3}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Describe your trading strategy..."
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Strategy Type *
            </label>
            <select
              {...register('type', { required: 'Strategy type is required' })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="ma_crossover">MA Crossover</option>
              <option value="momentum">Momentum</option>
              <option value="balanced_portfolio">Balanced Portfolio</option>
              <option value="ai_enhanced">AI Enhanced</option>
            </select>
            {errors.type && (
              <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Max Position Size (%)
            </label>
            <input
              type="number"
              step="0.1"
              min="0.1"
              max="100"
              {...register('maxPositionSize', {
                min: { value: 0.1, message: 'Minimum value is 0.1%' },
                max: { value: 100, message: 'Maximum value is 100%' },
                valueAsNumber: true,
              })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {errors.maxPositionSize && (
              <p className="mt-1 text-sm text-red-600">{errors.maxPositionSize.message}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Maximum portfolio allocation per asset/position. Recommended: 5% per asset (enables diversification across 20 different assets to reach 100% portfolio utilization)
            </p>
          </div>
        </div>

        {/* Risk Management Parameters */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Risk Level
            </label>
            <select
              {...register('riskLevel')}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="low">Low Risk</option>
              <option value="medium">Medium Risk</option>
              <option value="high">High Risk</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Overall risk tolerance for this strategy
            </p>
          </div>

          {/* Stop Loss Configuration - Enhanced */}
          <div className="space-y-4 p-4 border border-gray-200 rounded-lg">
            <h4 className="text-sm font-semibold text-gray-700">Stop Loss Configuration</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Stop Loss Percentage (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  placeholder="Optional (e.g., 5 for 5%)"
                  {...register('stopLossPercentage', {
                    min: { value: 0, message: 'Minimum value is 0%' },
                    max: { value: 100, message: 'Maximum value is 100%' },
                    valueAsNumber: true,
                  })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {errors.stopLossPercentage && (
                  <p className="mt-1 text-sm text-red-600">{errors.stopLossPercentage.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Stop Loss Type
                </label>
                <select
                  {...register('stopLossType')}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="fixed">Fixed (from entry price)</option>
                  <option value="trailing">Trailing (from peak price)</option>
                </select>
              </div>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded">
              {watch('stopLossType') === 'fixed' ? (
                <div>
                  <p className="text-sm text-blue-900">
                    <strong>Fixed Stop-Loss:</strong> Triggers when price drops {watch('stopLossPercentage') || 'X'}% below your <strong>entry price</strong>.
                  </p>
                  <p className="text-xs text-blue-700 mt-1">
                    ✓ Good for protecting against losses while letting winners run<br />
                    ✓ Predictable exit point<br />
                    ✓ Better in volatile markets
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-blue-900">
                    <strong>Trailing Stop-Loss:</strong> Triggers when price drops {watch('stopLossPercentage') || 'X'}% from the <strong>highest price reached</strong>.
                  </p>
                  <p className="text-xs text-blue-700 mt-1">
                    ✓ Protects profits by following price up<br />
                    ✓ Locks in gains during uptrends<br />
                    ✗ May exit early in volatile markets
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Take Profit Configuration - Enhanced */}
          <div className="space-y-4 p-4 border border-gray-200 rounded-lg">
            <h4 className="text-sm font-semibold text-gray-700">Take Profit Configuration</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Take Profit Percentage (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  placeholder="Optional (e.g., 10 for 10%)"
                  {...register('takeProfitPercentage', {
                    min: { value: 0.1, message: 'Minimum value is 0.1%' },
                    valueAsNumber: true,
                  })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {errors.takeProfitPercentage && (
                  <p className="mt-1 text-sm text-red-600">{errors.takeProfitPercentage.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Take Profit Type
                </label>
                <select
                  {...register('takeProfitType')}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="fixed">Fixed (from entry price)</option>
                  <option value="trailing">Trailing (from low price)</option>
                </select>
              </div>
            </div>

            <div className="bg-green-50 border-l-4 border-green-400 p-3 rounded">
              {watch('takeProfitType') === 'fixed' ? (
                <div>
                  <p className="text-sm text-green-900">
                    <strong>Fixed Take-Profit:</strong> Triggers when price rises {watch('takeProfitPercentage') || 'X'}% above your <strong>entry price</strong>.
                  </p>
                  <p className="text-xs text-green-700 mt-1">
                    ✓ Predictable profit targets<br />
                    ✓ Easy to calculate returns<br />
                    ✓ Most common approach
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-green-900">
                    <strong>Trailing Take-Profit:</strong> Triggers when price rises {watch('takeProfitPercentage') || 'X'}% from the <strong>lowest price after entry</strong>.
                  </p>
                  <p className="text-xs text-green-700 mt-1">
                    ✓ Captures rebounds after dips<br />
                    ✓ Advanced strategy<br />
                    ✗ Less predictable timing
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-6">
          <div className="flex items-center">
            <input
              type="checkbox"
              {...register('isActive')}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-700">
              Active (enable strategy execution)
            </label>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              {...register('isPaperTrading')}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-700">
              Paper Trading (simulate trades)
            </label>
          </div>
        </div>
      </div>

      {/* Strategy Configuration */}
      <div className="border-t border-gray-200 pt-6">
        {renderStrategyConfig()}
      </div>

      {/* Execution Parameters - SAME for Backtest/Paper/Live */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Execution Parameters</h3>
        <p className="text-sm text-gray-600 mb-4">
          ⚠️ <strong>Critical:</strong> These parameters apply IDENTICALLY to backtesting, paper trading, and live trading. 
          Ensure backtest results match real trading behavior.
        </p>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fee Rate (%)
            </label>
            <input
              type="number"
              step="0.001"
              min="0"
              max="1"
              {...register('config.feeRate', {
                min: { value: 0, message: 'Minimum value is 0%' },
                max: { value: 1, message: 'Maximum value is 1%' },
                valueAsNumber: true,
              })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {errors.config?.feeRate && (
              <p className="mt-1 text-sm text-red-600">{errors.config.feeRate.message}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Trading fee per transaction (default: 0.1%). Match your exchange fees.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Slippage Tolerance (%)
            </label>
            <input
              type="number"
              step="0.001"
              min="0"
              max="2"
              {...register('config.slippage', {
                min: { value: 0, message: 'Minimum value is 0%' },
                max: { value: 2, message: 'Maximum value is 2%' },
                valueAsNumber: true,
              })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {errors.config?.slippage && (
              <p className="mt-1 text-sm text-red-600">{errors.config.slippage.message}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Acceptable price deviation during execution (default: 0.1%)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Minimum Order Value (USD)
            </label>
            <input
              type="number"
              step="1"
              min="1"
              max="10000"
              {...register('config.minOrderValue', {
                min: { value: 1, message: 'Minimum value is $1' },
                max: { value: 10000, message: 'Maximum value is $10,000' },
                valueAsNumber: true,
              })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {errors.config?.minOrderValue && (
              <p className="mt-1 text-sm text-red-600">{errors.config.minOrderValue.message}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Minimum trade size to execute (default: $10). Prevents dust trades.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Maximum Order Value (USD)
            </label>
            <input
              type="number"
              step="any"
              min="10"
              max="10000000"
              {...register('config.maxOrderValue', {
                min: { value: 10, message: 'Minimum value is $10' },
                max: { value: 10000000, message: 'Maximum value is $10M' },
                valueAsNumber: true,
              })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {errors.config?.maxOrderValue && (
              <p className="mt-1 text-sm text-red-600">{errors.config.maxOrderValue.message}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Maximum trade size per order (default: $100k). Risk management cap.
            </p>
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
          disabled={isSubmitting || loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
          disabled={isSubmitting || loading}
        >
          {loading ? (
            <div className="flex items-center">
              <LoadingSpinner size="sm" color="white" className="mr-2" />
              Saving...
            </div>
          ) : (
            mode === 'create' ? 'Create Strategy' :
            mode === 'edit' ? 'Update Strategy' : 'Clone Strategy'
          )}
        </button>
      </div>
    </form>
  );
};

export default StrategyForm; 